import { api } from "@/lib/api/client";

/** `MeetingRoomEvent.approval_state` (#401). "" = not subject to approval. */
export type ApprovalState = "" | "within_allowance" | "pending" | "approved" | "rejected" | "expired";
export type SettingSource = "tenant" | "site" | "default";

/** One booking request on `/crm/booking-approvals/` — the company's admins decide it. */
export interface BookingApproval {
  id: number;
  title: string;
  room_id: number;
  room_name: string;
  floor_name: string;
  /** The room's site, IANA name (e.g. "Europe/Stockholm"): format start/end/expires_at in this zone, not
   *  the browser's. Optional only so a payload missing it still degrades to the browser's local time. */
  site_time_zone?: string;
  start: string;
  end: string;
  member_name: string;
  member_email: string;
  /** Decimal string, SEK excl. VAT; null when the price couldn't be looked up. */
  estimate_sek: string | null;
  state: ApprovalState;
  expires_at: string | null;
  decided_at: string | null;
  decided_by_name: string | null;
  note: string;
}

/** The open requests as the page uses them: the first page of `GET /crm/booking-approvals/` (soonest deadline
 *  first) and how many match in all — the response's `X-Total-Count` header (#434). */
export interface BookingApprovalPage {
  results: BookingApproval[];
  total: number;
}

export interface ApprovalSettings {
  requires_approval: boolean;
  requires_approval_source: SettingSource;
  /** Decimal string; "0.00" = no allowance. */
  monthly_allowance_sek: string;
  allowance_source: SettingSource;
  override_requires_approval: boolean | null;
  override_monthly_allowance_sek: string | null;
  can_edit: boolean;
  /** Plan R2: approval is switched on at all (false until MR E). */
  enabled: boolean;
  /** Plan R2: a paid booking by a non-admin of this company really waits (enabled, required, and it has an admin). */
  applies: boolean;
  /** A booking this close to its start lapses at the start if nobody approves it (spec §5). */
  short_notice_minutes: number;
}

/** The company's own override; null = use the workspace default. */
export interface ApprovalSettingsInput {
  requires_approval: boolean | null;
  monthly_allowance_sek: string | null;
}

export const bookingApprovalsApi = {
  list: (state: "pending" | "decided" = "pending") =>
    api.get<BookingApproval[]>("/crm/booking-approvals/", { params: { state } }),
  approve: (id: number) => api.post<BookingApproval>(`/crm/booking-approvals/${id}/approve/`),
  reject: (id: number, note: string) => api.post<BookingApproval>(`/crm/booking-approvals/${id}/reject/`, { note }),
  getSettings: () => api.get<ApprovalSettings>("/crm/booking-approvals/settings/"),
  putSettings: (input: ApprovalSettingsInput) => api.put<ApprovalSettings>("/crm/booking-approvals/settings/", input),
};
